import React, { useState, useEffect } from "react";
import { Text, Heading } from "../../../../components/common/typography";
import { Badge } from "../../../../components/common/badge";
import { documentsService } from "../../../api_client";
import type { DocumentDto } from "../../../../api/api";
import { Button } from "../../../../components/common/button";
import { Loader } from "../../../../components/common/loader";
import { showErrorToast, showSuccessToast } from "../../../../utils/toasts";
import { IconCircleCheck, IconCircleX, IconDownload, IconX } from "@tabler/icons-react";
interface DocumentPreviewModalProps {
    document: DocumentDto;
    open: boolean;
    onClose: () => void;
    onApprove?: () => void;
    onReject?: () => void;
    canTakeAction: boolean;
    isApproving?: boolean;
    isRejecting?: boolean;
}
const getDocumentTypeDisplayName = (type: string): string => {
    switch (type) {
        case "CR":
            return "Commercial Registration";
        case "TAX_CARD":
            return "Tax Card";
        default:
            return type;
    }
};
const getStatusBadge = (status: string) => {
    switch (status) {
        case "PENDING":
            return <Badge label="Pending" state="pending"/>;
        case "APPROVED":
            return <Badge label="Approved" state="success"/>;
        case "REJECTED":
            return <Badge label="Rejected" state="error"/>;
        default:
            return <Badge label={status} state="pending"/>;
    }
};
export const DocumentPreviewModal: React.FC<DocumentPreviewModalProps> = ({ document, open, onClose, onApprove, onReject, canTakeAction, isApproving = false, isRejecting = false, }) => {
    const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
    const [isLoadingUrl, setIsLoadingUrl] = useState(false);
    useEffect(() => {
        if (open && document) {
            loadDownloadUrl();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, document]);
    const loadDownloadUrl = async () => {
        setIsLoadingUrl(true);
        try {
            const response = await documentsService.getDocumentDownloadUrl(document.id);
            setDownloadUrl(response.data.url || null);
        }
        catch (error) {
            console.error("Failed to load download URL:", error);
            showErrorToast("Failed to load document preview");
        }
        finally {
            setIsLoadingUrl(false);
        }
    };
    const handleDownload = () => {
        if (downloadUrl) {
            const link = window.document.createElement("a");
            link.href = downloadUrl;
            link.download = document.fileName;
            link.target = "_blank";
            window.document.body.appendChild(link);
            link.click();
            window.document.body.removeChild(link);
            showSuccessToast("Download started");
        }
    };
    if (!open)
        return null;
    const isPdf = document.contentType === "application/pdf" ||
        document.fileName.endsWith(".pdf");
    const isImage = document.contentType.startsWith("image/") ||
        /\.(jpg|jpeg|png|gif)$/i.test(document.fileName);
    return (<div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-bgDefault rounded-xl w-full max-w-4xl max-h-[90vh] flex flex-col m-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-uiSecondary">
          <div className="flex-1">
            <Heading variant="h3" tone="dark" className="mb-1">
              {getDocumentTypeDisplayName(document.type)}
            </Heading>
            <Text variant="body" tone="body">
              {document.fileName}
            </Text>
          </div>
          <div className="flex items-center gap-3">
            {getStatusBadge(document.status)}
            <button onClick={onClose} className="p-2 hover:bg-uiSecondary rounded-lg transition-colors" aria-label="Close">
              <IconX size={24} style={{
            color: "#6B7280"
        }}/>
            </button>
          </div>
        </div>

        {/* Preview Content */}
        <div className="flex-1 overflow-auto p-6">
          {isLoadingUrl ? (<div className="flex items-center justify-center h-96">
              <Loader text="Loading document..."/>
            </div>) : downloadUrl ? (<div className="w-full h-full">
              {isPdf ? (<iframe src={downloadUrl} className="w-full h-[600px] border border-uiSecondary rounded-lg" title="Document preview"/>) : isImage ? (<img src={downloadUrl} alt={document.fileName} className="max-w-full max-h-[600px] mx-auto rounded-lg"/>) : (<div className="flex flex-col items-center justify-center h-96">
                  <Text variant="body" tone="body" className="mb-4">
                    Preview not available for this file type
                  </Text>
                  <Button variant="primary" onClick={handleDownload}>
                    <IconDownload size={18} style={{
                    marginRight: 1
                }}/>
                    Download to View
                  </Button>
                </div>)}
            </div>) : (<div className="flex items-center justify-center h-96">
              <Text variant="body" className="text-accentError">
                Failed to load document preview
              </Text>
            </div>)}
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between p-6 border-t border-uiSecondary">
          <div className="flex items-center gap-4">
            <Text variant="bodyXs" tone="body">
              File Size:{" "}
              {document.fileSize
            ? `${(document.fileSize / 1024).toFixed(2)} KB`
            : "Unknown"}
            </Text>
            {document.rejectionReason && (<Text variant="bodyXs" className="text-accentError">
                Rejection Reason: {document.rejectionReason}
              </Text>)}
          </div>
          <div className="flex items-center gap-3">
            {downloadUrl && (<Button variant="secondary" onClick={handleDownload}>
                <IconDownload size={18} style={{
                marginRight: 1
            }}/>
                Download
              </Button>)}
            {canTakeAction && (<>
                <Button variant="primary" onClick={onApprove} disabled={isApproving || isRejecting} className="flex items-center gap-1">
                  <IconCircleCheck size={18}/>
                  {isApproving ? "Approving..." : "Approve"}
                </Button>
                <Button variant="secondary" onClick={onReject} disabled={isApproving || isRejecting} className="flex items-center gap-1">
                  <IconCircleX size={18}/>
                  Reject
                </Button>
              </>)}
          </div>
        </div>
      </div>
    </div>);
};
